<template>
  <v-row v-if="props.checklist && props.checklist.length" class="section-container">
    <v-col cols="12" class="header-with-select-box">
      <h3 class="text-primary weight-600 mb-4">{{ props.title }}</h3>
    </v-col>

    <v-col cols="12">
      <v-row v-for="(group, gIndex) in groupedQuestions" :key="group.header || gIndex" class="">
        <v-col v-if="group.header && groupHasVisibleQuestions(group)" cols="12" class="text-body-1 font-weight-bold">
          {{ group.header }}
        </v-col>

        <v-col v-if="groupHasVisibleQuestions(group)" cols="12">
          <v-row v-for="currentRequirement in group.questions" :key="currentRequirement.id" cols="12">
            <v-col
              v-if="!currentRequirement.invisibleInPortal"
              cols="12"
              class="mt-n3"
              :class="{ 'policy-flagged-question': currentRequirement.policyReview?.alignsWithPolicy === 'NO' }">
              <p class="text-body-3-2 weight-600 text-primary">
                {{ currentRequirement.label }}
              </p>

              <div v-for="(option, index) in currentRequirement.options" :key="index">
                <v-text-field
                  v-model="option.label"
                  :type="option.nodeType"
                  :placeholder="currentRequirement.label"
                  readonly
                  disabled
                  :class="{
                    'lh--force-white-background mt-2': true,
                    'policy-flagged-input': currentRequirement.policyReview?.alignsWithPolicy === 'NO',
                  }">
                </v-text-field>
              </div>

              <div v-if="currentRequirement.llmPrediction" class="text-body-3-1 text-color-blue-gray mb-4">
                <div v-if="currentRequirement.llmPrediction.isHumanReviewRequested">
                  <StatusLabel :text="'Review Needed'" :color="'error'" />
                </div>
                <span
                  v-if="currentRequirement.llmPrediction.context"
                  class="text-color-blue-gray weight-400 mt-n2 text-body-3-1">
                  Context: {{ currentRequirement.llmPrediction.context }}
                </span>
              </div>

              <div v-if="currentRequirement.policyReview?.alignsWithPolicy === 'NO'" class="policy-warning-inline mb-4">
                <v-icon color="error" size="18" class="mr-1">mdi-alert-circle</v-icon>
                <span class="policy-warning-text">The current answer is unlikely to be approved.</span>
              </div>

              <v-divider v-if="!isLastRequirement(group, currentRequirement)" class="mb-3 mt-6"></v-divider>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import StatusLabel from "@/components/StatusLabel.vue";

const payerQuestions = ref([]);

const props = defineProps({
  checklist: {
    type: Array,
    default() {
      return [];
    },
  },
  llmPrediction: {
    type: Array,
    default() {
      return [];
    },
  },
  policyReviews: {
    type: Array,
    default() {
      return [];
    },
  },
  originalPayerQuestions: {
    type: Array,
    default() {
      return [];
    },
  },
  title: {
    type: String,
    default: "",
  },
});

// Watch for checklist AND policyReviews changes to process questions
watch(
  [() => props.checklist, () => props.policyReviews],
  ([newChecklist, newPolicyReviews]) => {
    if (!newChecklist || newChecklist.length === 0) return;

    // Clear and rebuild payerQuestions
    payerQuestions.value = [];

    for (const requirement of newChecklist) {
      let payerQuestion = { ...requirement };

      // Match LLM prediction (confidence/review needed)
      if (props.llmPrediction && props.llmPrediction.length > 0) {
        payerQuestion.llmPrediction = props.llmPrediction.find((prediction) => prediction.answerId === requirement.id);
      }

      // Match policy review (policy alignment check)
      if (newPolicyReviews && newPolicyReviews.length > 0) {
        const matchedReview = newPolicyReviews.find((review) => review.questionId === requirement.id);
        payerQuestion.policyReview = matchedReview;
      }

      let originalQuestion = props.originalPayerQuestions.find(
        (originalPayerQuestion) => originalPayerQuestion.requirementRuleName === requirement.id
      );
      payerQuestion.invisibleInPortal = originalQuestion ? originalQuestion.invisibleInPortal : false;
      payerQuestion.sectionHeader = originalQuestion ? originalQuestion.sectionHeader : null;
      payerQuestions.value.push(payerQuestion);
    }
  },
  { immediate: true, deep: true }
);

const groupedQuestions = computed(() => {
  const map = new Map();

  payerQuestions.value.forEach((requirement) => {
    const header = requirement.sectionHeader || "no-header";
    if (!map.has(header)) {
      map.set(header, []);
    }
    map.get(header).push(requirement);
  });

  return Array.from(map, ([header, questions]) => ({
    header: header === "no-header" ? null : header,
    questions,
  }));
});

function isRequirementApplicable(requirement) {
  return !requirement.invisibleInPortal;
}

function groupHasVisibleQuestions(group) {
  return group.questions.some((req) => isRequirementApplicable(req));
}

function isLastRequirement(group, requirement) {
  const visibleQuestions = group.questions.filter((req) => !req.invisibleInPortal);
  return visibleQuestions[visibleQuestions.length - 1].id === requirement.id;
}
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";

.policy-flagged-question {
  background-color: rgba(244, 67, 54, 0.04);
  border-left: 3px solid #f44336;
  border-radius: 4px;
  padding-left: 12px !important;
  margin-left: -12px;
}

.policy-flagged-input :deep(.v-field) {
  border: 1px solid #f44336 !important;
  border-radius: 4px;
}

.policy-warning-inline {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.policy-warning-text {
  color: #d32f2f;
  font-size: 0.875rem;
  font-weight: 500;
}
</style>
